import React, { useEffect, useState, useRef } from 'react';
import { api } from '@/shared/lib/api';
import { useHapticFeedback } from '@/shared/hooks/useHapticFeedback';

export interface DailyGoalVictoryWidgetProps {
  cardsAnsweredInSession?: number;
  style?: React.CSSProperties;
}

interface DailyStatsResponse {
  answered_cards: number;
  daily_goal: number;
  current_streak?: number;
}

const CIRCUMFERENCE = 226.195; // 2 * Math.PI * 36

export function DailyGoalVictoryWidget({
  cardsAnsweredInSession = 0,
  style,
}: DailyGoalVictoryWidgetProps) {
  const { notify } = useHapticFeedback();
  const [stats, setStats] = useState<DailyStatsResponse | null>(null);
  const [animatedCount, setAnimatedCount] = useState<number>(0);
  const [animatedOffset, setAnimatedOffset] = useState<number>(CIRCUMFERENCE);
  const notifiedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    api.get<DailyStatsResponse>('/stats/daily')
      .then((data) => {
        if (!isMounted || !data) return;
        setStats(data);

        const goal = data.daily_goal > 0 ? data.daily_goal : 20;
        const total = data.answered_cards || 0;
        const prev = Math.max(0, total - cardsAnsweredInSession);

        // Начальное состояние — с предыдущего значения
        setAnimatedCount(prev);
        const prevRatio = Math.min(1, prev / goal);
        setAnimatedOffset(CIRCUMFERENCE * (1 - prevRatio));

        // Анимация разгона к текущему итоговому значению
        const timer = setTimeout(() => {
          if (!isMounted) return;
          setAnimatedCount(total);
          const totalRatio = Math.min(1, total / goal);
          setAnimatedOffset(CIRCUMFERENCE * (1 - totalRatio));

          // Если норма была закрыта именно в этой сессии — тактильный отклик триумфа
          if (prev < goal && total >= goal && !notifiedRef.current) {
            notifiedRef.current = true;
            try {
              notify('success');
            } catch {
              // Игнорируем в браузерах без Telegram WebApp
            }
          }
        }, 120);

        return () => clearTimeout(timer);
      })
      .catch(() => {
        // При ошибке сети плашка не ломает экран, просто тихо не рендерится
      });

    return () => {
      isMounted = false;
    };
  }, [cardsAnsweredInSession, notify]);

  if (!stats) return null;

  const goal = stats.daily_goal > 0 ? stats.daily_goal : 20;
  const total = stats.answered_cards || 0;
  const isGoalMet = total >= goal;
  const isOverdrive = total > goal;
  const overdriveCount = total - goal;
  const remaining = Math.max(0, goal - total);
  const percent = Math.min(100, Math.round((total / goal) * 100));

  // Цветовая гамма спидометра
  const strokeColor = isOverdrive
    ? '#f59e0b' // Янтарный форсаж
    : isGoalMet
      ? '#00e676' // Изумрудный финиш
      : 'var(--theme-primary, #7c4dff)'; // В процессе

  const containerBg = isOverdrive
    ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.04) 100%)'
    : isGoalMet
      ? 'linear-gradient(135deg, rgba(0, 230, 118, 0.12) 0%, rgba(16, 185, 129, 0.04) 100%)'
      : 'var(--theme-bg-card, #1a1a2e)';

  const containerBorder = isOverdrive
    ? '1px solid rgba(245, 158, 11, 0.35)'
    : isGoalMet
      ? '1px solid rgba(0, 230, 118, 0.35)'
      : '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))';

  return (
    <div
      style={{
        background: containerBg,
        border: containerBorder,
        borderRadius: 16,
        padding: '12px 14px',
        maxWidth: 320,
        margin: '0 auto 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxShadow: isGoalMet ? '0 4px 20px rgba(0, 230, 118, 0.15)' : '0 4px 16px rgba(0, 0, 0, 0.2)',
        textAlign: 'left',
        ...style,
      }}
    >
      {/* Мини-спидометр с живой дугой */}
      <div
        style={{
          position: 'relative',
          width: 48,
          height: 48,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width="48"
          height="48"
          viewBox="0 0 90 90"
          style={{ transform: 'rotate(-90deg)' }}
        >
          {/* Фоновый серый контур */}
          <circle
            cx="45"
            cy="45"
            r="36"
            stroke="rgba(255, 255, 255, 0.08)"
            strokeWidth="8"
            fill="none"
          />
          {/* Активная цветная дуга с плавной анимацией */}
          <circle
            cx="45"
            cy="45"
            r="36"
            stroke={strokeColor}
            strokeWidth="8"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={animatedOffset}
            strokeLinecap="round"
            fill="none"
            style={{
              transition: 'stroke-dashoffset 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), stroke 0.3s ease',
            }}
          />
        </svg>

        {/* Число по центру кружка */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: 'var(--theme-text, #ffffff)',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
            }}
          >
            {animatedCount}
          </span>
        </div>
      </div>

      {/* Текстовая колонка рядом со спидометром */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--theme-text, #ffffff)' }}>
            Дневная норма:
          </span>
          {isOverdrive ? (
            <span
              style={{
                fontSize: 10,
                fontWeight: 800,
                padding: '2px 6px',
                borderRadius: 6,
                background: 'rgba(245, 158, 11, 0.25)',
                color: '#f59e0b',
                lineHeight: 1.2,
              }}
            >
              ФОРСАЖ 🔥
            </span>
          ) : isGoalMet ? (
            <span
              style={{
                fontSize: 10,
                fontWeight: 800,
                padding: '2px 6px',
                borderRadius: 6,
                background: 'rgba(0, 230, 118, 0.25)',
                color: '#00e676',
                lineHeight: 1.2,
              }}
            >
              ВЫПОЛНЕНА! 🎉
            </span>
          ) : (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 6,
                background: 'rgba(255, 255, 255, 0.08)',
                color: 'var(--theme-text-secondary, rgba(255, 255, 255, 0.6))',
                lineHeight: 1.2,
              }}
            >
              {percent}%
            </span>
          )}
        </div>

        {/* Поясняющая накопительная строка */}
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: isOverdrive
              ? '#f59e0b'
              : isGoalMet
                ? '#00e676'
                : 'var(--theme-text-secondary, rgba(255, 255, 255, 0.6))',
            lineHeight: 1.3,
          }}
        >
          {cardsAnsweredInSession > 0 ? `+${cardsAnsweredInSession} за урок • ` : ''}
          {total} из {goal}
          {isOverdrive
            ? ` (+${overdriveCount} сверх)`
            : isGoalMet
              ? ' • Цель закрыта!'
              : ` • ещё ${remaining}`}
        </div>
      </div>
    </div>
  );
}
